import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, where, doc, getDoc, setDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";
import { useApp } from "../App";
import { useNavigate } from "react-router-dom";
import { CATEGORIES } from "../data/items";
import KeptiIcon from "../components/KeptiIcon";
import { IconSearch, IconHeart, IconNoListing } from "../components/Icons";

const SIZES = ["1Y","2Y","4Y","6Y","8Y","10Y","12Y","14Y","S","M","L","XL","2XL","3XL"];

export default function Browse() {
  const { t, user, setShowPost } = useApp();
  const [listings, setListings] = useState([]);
  const [wanted, setWanted] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeSize, setActiveSize] = useState(null);
  const [showSizeSheet, setShowSizeSheet] = useState(false);
  const [search, setSearch] = useState("");
  const [saved, setSaved] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(
      collection(db, "listings"),
      where("status", "==", "active"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, snap => {
      setListings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, "wanted"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setWanted(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then(d => {
      if (d.exists()) {
        const savedIds = d.data().saved || [];
        const savedMap = {};
        savedIds.forEach(id => savedMap[id] = true);
        setSaved(savedMap);
      }
    });
  }, [user]);

  const toggleSave = async (e, listingId) => {
    e.stopPropagation();
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    const isSaved = saved[listingId];
    setSaved(prev => ({ ...prev, [listingId]: !isSaved }));
    try {
      await setDoc(userRef, {
        saved: isSaved ? arrayRemove(listingId) : arrayUnion(listingId)
      }, { merge: true });
    } catch {
      setSaved(prev => ({ ...prev, [listingId]: isSaved }));
    }
  };

  const filtered = listings.filter(l => {
    const matchCat = activeFilter === "all" || l.category === activeFilter;
    const matchSize = !activeSize || l.size === activeSize;
    const matchSearch = !search || l.itemName?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSize && matchSearch;
  });

  const conditionLabel = (c) => {
    if (c === "likeNew") return t.likeNew;
    if (c === "good") return t.good;
    return t.used;
  };

  const conditionClass = (c) => {
    if (c === "likeNew") return "badge-new";
    if (c === "good") return "badge-good";
    return "badge-used";
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = Date.now() - ts.toMillis();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "<1h";
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  const initials = (name) => name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";

  return (
    <>
      <div className="topbar">
        <div className="topbar-row">
          <div className="topbar-logo">
            <KeptiIcon size={28} />
            <span className="brand-wordmark">kepti</span>
          </div>
        </div>
        <div className="search-bar">
          <IconSearch />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.6)", cursor: "pointer", fontSize: 16 }}>×</button>
          )}
        </div>
      </div>

      <div className="filter-strip">
        <div
          className={`chip ${activeFilter === "all" ? "active" : ""}`}
          onClick={() => setActiveFilter("all")}
        >
          {t["all"] || "All"}
        </div>
        <div
          className={`chip ${activeSize ? "active" : ""}`}
          style={activeSize ? { backgroundColor: "var(--red)", borderColor: "var(--red)" } : {}}
          onClick={() => activeSize ? setActiveSize(null) : setShowSizeSheet(prev => !prev)}
        >
          {activeSize ? `${activeSize} ×` : (t.size || "Size")}
        </div>
        {CATEGORIES.map(cat => (
          <div key={cat} className={`chip ${activeFilter === cat ? "active" : ""}`} onClick={() => setActiveFilter(cat)}>
            {t[cat] || cat}
          </div>
        ))}
      </div>

      {showSizeSheet && (
        <>
          <div onClick={() => setShowSizeSheet(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }} />
          <div style={{
            position: "relative",
            zIndex: 100,
            background: "var(--offwhite)",
            borderBottom: "1px solid var(--border)",
            padding: "16px 20px 20px",
          }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {SIZES.map(size => (
                <button
                  key={size}
                  onClick={() => { setActiveSize(size); setShowSizeSheet(false); }}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 8,
                    border: `1.5px solid ${activeSize === size ? "var(--navy)" : "var(--border)"}`,
                    background: activeSize === size ? "var(--navy)" : "white",
                    color: activeSize === size ? "var(--cream)" : "var(--navy)",
                    fontWeight: 500,
                    fontSize: 14,
                    cursor: "pointer",
                    minWidth: 56,
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
            {activeSize && (
              <button
                onClick={() => { setActiveSize(null); setShowSizeSheet(false); }}
                style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--border)", background: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer" }}
              >
                {t.clearSize || "Clear size filter"}
              </button>
            )}
          </div>
        </>
      )}

      <div className="section-header">
        <span className="section-title">{t.latest}</span>
        <span className="see-all">{filtered.length} items</span>
      </div>

      <div className="listings-grid">
        {filtered.length === 0 && (
          <div style={{ gridColumn: "1/-1", padding: "40px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 14, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <IconNoListing />
            {t.noListings}
          </div>
        )}
        {filtered.map(listing => (
          <div key={listing.id} className="listing-card" onClick={() => navigate(`/listing/${listing.id}`)}>
            <div className="listing-img">
              {listing.imageUrl ? <img src={listing.imageUrl} alt={listing.itemName} /> : <IconNoListing />}
              <span className={`condition-badge ${conditionClass(listing.condition)}`}>
                {conditionLabel(listing.condition)}
              </span>
            </div>
            <div className="listing-body">
              <div className="listing-name">{listing.itemName}</div>
              <div className="listing-meta">{listing.size} · {t[listing.category] || listing.category}</div>
              <div className="listing-footer">
                <span className="listing-price">€{listing.price}</span>
                <button className={`wish-btn ${saved[listing.id] ? "liked" : ""}`} onClick={e => toggleSave(e, listing.id)}>
                  <IconHeart filled={!!saved[listing.id]} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {wanted.length > 0 && (
        <>
          <div className="section-header">
            <span className="section-title">{t.lookingFor}</span>
            <span className="see-all">{t.seeAll}</span>
          </div>
          <div className="wants-section">
            {wanted.slice(0, 5).map(w => (
              <div key={w.id} className="want-card">
                <div className="want-avatar">{initials(w.userName)}</div>
                <div className="want-body">
                  <div className="want-name">{w.description}</div>
                  <div className="want-detail">{w.userName}</div>
                </div>
                <div className="want-time">{timeAgo(w.createdAt)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ height: 100 }} />
      <button className="fab" onClick={() => setShowPost(true)} title={t.sell}>+</button>
    </>
  );
}
